import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import useScheduleCalendar from 'api/calendar/useScheduleCalendar';
import {
  CalendarAgendaItem,
  CalendarGrouping,
  CalendarSession,
} from 'api/calendar/types';
import { RowDisplay } from './scheduleView.types';
import AgendaRow from './AgendaRow';
import SkeletonAgendaRow from './SkeletonAgendaRow';
import { formatHeading } from './scheduleViewUtils';

interface DaySectionProps {
  readonly date: string;
  readonly locale: string;
  readonly grouping: CalendarGrouping;
  readonly categoryIds: ReadonlyArray<string>;
  readonly vibeIds: ReadonlyArray<string>;
  /** Rows with MORE than this many sessions collapse into a per-row dropdown. `null` disables
   *  collapsing entirely — every session renders as its own inline row. */
  readonly collapseThreshold: number | null;
  readonly rowDisplay: RowDisplay;
  readonly onBook: (item: CalendarAgendaItem, session: CalendarSession) => void;
  /** Active filter's display name, used in the empty state ("No {label} experiences scheduled"). */
  readonly filterLabel?: string | null;
  /** Mobile viewport — rows tap to book (CTA hidden); threaded to AgendaRow. */
  readonly isMobile?: boolean;
  /** Reports load state up so the page only appends the NEXT day once this one resolves. */
  readonly onLoaded?: (date: string) => void;
  /** Hide the date heading (used for the first/anchor day when the page renders its own). */
  readonly showHeading?: boolean;
}

interface RenderUnit {
  readonly item: CalendarAgendaItem;
  readonly sessions: ReadonlyArray<CalendarSession>;
  readonly collapsed: boolean;
}

/** Group sessions (already sorted ascending by start time) into one bucket per start time, so a
 *  30-min and a 1-hour slot both at 6:00am become a single row that lists both durations. The Map
 *  preserves insertion order, so the buckets stay in ascending start-time order. */
const groupByStartTime = (
  sessions: ReadonlyArray<CalendarSession>,
): CalendarSession[][] => {
  const byStartTime = sessions.reduce<Map<string, CalendarSession[]>>(
    (acc, session) => {
      const bucket = acc.get(session.startDateTime);
      if (bucket) bucket.push(session);
      else acc.set(session.startDateTime, [session]);
      return acc;
    },
    new Map(),
  );
  return Array.from(byStartTime.values());
};

const explode = (
  item: CalendarAgendaItem,
  threshold: number | null,
): RenderUnit[] => {
  const sessions = [...item.sessions]
    .filter(
      (session) => session.state !== 'PAST' && session.state !== 'PAST_CUTOFF',
    )
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
  if (sessions.length === 0) return [];
  // One inline row per START TIME: sessions sharing a start time merge into a single row that
  // lists all their durations. Collapse into a dropdown only when there are too many *slots*
  // (distinct start times) to list individually — so a single slot never becomes a lone dropdown,
  // and a listing whose extra sessions are just duplicate-time durations isn't collapsed on raw
  // session count. A `null` threshold disables collapsing entirely.
  const slots = groupByStartTime(sessions);
  const collapsed = threshold != null && slots.length > threshold;
  if (collapsed) return [{ item, sessions, collapsed: true }];
  return slots.map((slotSessions) => ({
    item,
    sessions: slotSessions,
    collapsed: false,
  }));
};

const leadTime = (unit: RenderUnit) => unit.sessions[0]?.startDateTime ?? '';

const SKELETON_KEYS = ['s1', 's2', 's3', 's4'];

/**
 * One day in the infinite-scroll schedule list: its own calendar request (so the page keeps ONE
 * request per day and never refetches), a sticky date heading, and the grouped, exploded agenda
 * with sticky group headers (Morning/Afternoon/Evening or category).
 */
const DaySection = ({
  date,
  locale,
  grouping,
  categoryIds,
  vibeIds,
  collapseThreshold,
  rowDisplay,
  filterLabel,
  isMobile,
  onBook,
  onLoaded,
  showHeading = true,
}: DaySectionProps) => {
  const { t: translate } = useTranslation();

  const { day, isLoadingCalendar } = useScheduleCalendar({
    date,
    grouping,
    categoryIds,
    vibeIds,
  });

  const sections = useMemo(
    () =>
      (day?.groups ?? [])
        .map((group) => ({
          key: group.key,
          label: group.label,
          units: group.items
            .flatMap((item) => explode(item, collapseThreshold))
            .sort((a, b) => leadTime(a).localeCompare(leadTime(b)))
            .map((unit, index) => ({
              ...unit,
              key: `${group.key}-${unit.item.subjectId}-${
                unit.collapsed ? 'collapsed' : leadTime(unit)
              }-${index}`,
            })),
        }))
        .filter((section) => section.units.length > 0),
    [day, collapseThreshold],
  );

  const totalUnits = sections.reduce(
    (sum, section) => sum + section.units.length,
    0,
  );

  // Once this day resolves (success or empty), let the page consider appending the next day.
  useEffect(() => {
    if (!isLoadingCalendar) onLoaded?.(date);
  }, [isLoadingCalendar, date, onLoaded]);

  const headingId = `schedule-view__date-heading-${date}`;
  const headingText = formatHeading(date, locale, {
    today: translate('today', { defaultValue: 'Today' }),
    tomorrow: translate('tomorrow', { defaultValue: 'Tomorrow' }),
  });

  return (
    <DaySectionBlock
      className="schedule-view__day-section"
      data-schedule-day={date}
      role="region"
      aria-labelledby={showHeading ? headingId : undefined}
      aria-label={showHeading ? undefined : headingText}
    >
      {showHeading && (
        <DateHeading id={headingId} className="schedule-view__date-heading">
          {headingText}
        </DateHeading>
      )}

      {isLoadingCalendar && (
        <div
          className="schedule-view__loading"
          aria-busy="true"
          aria-label={translate('loading', { defaultValue: 'Loading…' })}
        >
          {SKELETON_KEYS.map((skeletonKey) => (
            <SkeletonAgendaRow key={skeletonKey} />
          ))}
        </div>
      )}

      {!isLoadingCalendar && totalUnits === 0 && (
        <DayEmpty className="schedule-view__day-empty">
          {filterLabel
            ? translate('noCategoryExperiencesScheduled', {
                defaultValue: 'No {{category}} experiences scheduled',
                category: filterLabel,
              })
            : translate('noExperiencesScheduled', {
                defaultValue: 'No experiences scheduled',
              })}
        </DayEmpty>
      )}

      {!isLoadingCalendar &&
        sections.map((section) => (
          <Section key={section.key} className="schedule-view__section">
            {section.units.map((unit) => (
              <AgendaRow
                key={unit.key}
                item={unit.item}
                sessions={unit.sessions}
                collapsed={unit.collapsed}
                locale={locale}
                isMobile={isMobile}
                onBook={onBook}
                rowDisplay={rowDisplay}
              />
            ))}
          </Section>
        ))}
    </DaySectionBlock>
  );
};

// Memoised so a re-render of the parent (day strip selection, scroll-spy URL rewrite, etc.) only
// reconciles days whose inputs actually changed. Relies on stable identities for `categoryIds`,
// `vibeIds`, `rowDisplay`, `onBook`, and `onLoaded` from the ScheduleViewPage owner.
export default React.memo(DaySection);

/* One day in the infinite-scroll list: its sticky date heading + grouped agenda. */
const DaySectionBlock = styled.div`
  width: 100%;
  /* Carry the client background down to the sticky DateHeading so it stays opaque. */
  background: inherit;
`;

/* Compact "nothing scheduled" line for a single day inside the multi-day list. */
const DayEmpty = styled.div`
  padding: 16px 0 24px;
  color: var(--way-colors-tertiaryTextColor);
  font-family: inherit;
  font-size: 15px;
`;

const DateHeading = styled.h2`
  position: sticky;
  top: var(--schedule-sticky-top, 96px);
  z-index: 1;
  font-family: inherit;
  font-weight: 400;
  font-size: 18px;
  line-height: 1.4;
  letter-spacing: -0.2px;
  margin: 0;
  padding: 16px 0 12px;
  color: var(--way-colors-primaryTextColor);
  background: inherit;
  width: 100%;

  @media all and (min-width: 768px) {
    font-size: 20px;
    line-height: 1.5;
    padding: 24px 0;
  }
`;

/* A time-of-day / category group rendered as a labelled section (driven by the API grouping). */
const Section = styled.section`
  width: 100%;
`;
